<template>
    <v-container class="clublink-galaxy">
      <v-card class="rounded-xl elevation-12 glass clublink-card">
        <!-- HEADER -->
        <v-card-title class="clublink-header">
          <div class="d-flex align-center">
            <v-avatar size="34" class="mr-3" style="background: linear-gradient(135deg, #d4af37, #c9a961);">
              <v-icon style="color: #2d4a3e;">mdi-briefcase-outline</v-icon>
            </v-avatar>
            <div>
              <div class="title font-weight-bold white--text" style="font-family: 'Playfair Display', serif;">Products</div>
              <div class="caption white--text" style="opacity:.85">Explore your product workspaces</div>
            </div>
          </div>
          <v-spacer/>
          <v-text-field
            v-model="search"
            dense outlined hide-details
            prepend-inner-icon="mdi-magnify"
            placeholder="Search products"
            class="search"
          />
        </v-card-title>
  
        <v-divider class="gold-divider"/>
  
        <!-- QUICK FILTERS -->
        <v-card-text class="pt-3 pb-0">
          <div class="d-flex align-center flex-wrap ga-2">
            <span class="caption grey--text mr-2 font-weight-medium">Status:</span>
            <v-chip
              v-for="s in statuses"
              :key="s"
              small
              :color="statusFilter===s ? statusColor(s) : 'rgba(212, 175, 55, 0.1)'"
              :text-color="statusFilter===s ? 'white' : '#2d4a3e'"
              class="mr-2 mb-2 text-capitalize status-chip"
              :class="{'active-status-chip': statusFilter===s}"
              @click="statusFilter = s"
            >
              <v-icon left x-small v-if="s!=='all'">{{ statusIcon(s) }}</v-icon>
              {{ s }}
            </v-chip>
  
            <v-spacer/>
  
            <v-select
              v-model="sortKey"
              :items="sortItems"
              dense outlined hide-details
              style="max-width: 190px"
              class="clublink-input-small"
              color="#d4af37"
            />
          </div>
        </v-card-text>
  
        <!-- LIST -->
        <v-list two-line class="pt-0">
          <v-skeleton-loader v-if="loading" type="list-item@5" class="px-4 py-2"/>
          <template v-else>
            <v-list-item
              v-for="p in sortedFiltered"
              :key="p.id"
              @click="$router.push({ name:'StaffProductDetail', params:{ id:p.id } })"
              class="rounded-lg mb-2 hoverable px-4"
            >
              <v-list-item-avatar>
                <v-avatar size="32" :color="avatarColor(p.name)">
                  <span class="white--text">{{ initials(p.name) }}</span>
                </v-avatar>
              </v-list-item-avatar>
  
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium dark-green--text">{{ p.name }}</v-list-item-title>
                <v-list-item-subtitle class="truncate">{{ p.summary }}</v-list-item-subtitle>
              </v-list-item-content>
  
              <div class="mr-3 text-right hidden-sm-and-down">
                <div class="caption grey--text">Updated</div>
                <div class="caption dark-green--text font-weight-medium">{{ prettyTs(p.updatedAt) }}</div>
              </div>
  
              <v-chip
                x-small class="text-capitalize"
                :color="statusColor(p.status)"
                text-color="white"
              >
                {{ p.status }}
              </v-chip>
  
              <v-icon class="ml-3 gold--text">mdi-chevron-right</v-icon>
            </v-list-item>
  
            <div v-if="!sortedFiltered.length" class="pa-6 text-center grey--text">
              <v-icon size="40" class="mb-2 gold--text">mdi-briefcase-search</v-icon>
              <div>No products match your filters.</div>
            </div>
          </template>
        </v-list>
      </v-card>
    </v-container>
  </template>
  
  <script>
  import { db } from '@/firebase'
  import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
  
  export default {
    name: 'StaffProducts',
    data () {
      return {
        loading: true,
        products: [],
        unsub: null,
        search: '',
        statusFilter: 'all',
        statuses: ['all', 'active', 'paused', 'archived'],
        sortKey: 'updated-desc',
        sortItems: [
          { text: 'Updated (newest)', value: 'updated-desc' },
          { text: 'Updated (oldest)', value: 'updated-asc' },
          { text: 'Name (A → Z)', value: 'name-asc' },
          { text: 'Name (Z → A)', value: 'name-desc' }
        ]
      }
    },
    created () {
      const qRef = query(collection(db,'products'), orderBy('updatedAt','desc'))
      this.unsub = onSnapshot(qRef, snap => {
        this.products = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.loading = false
      }, () => { this.loading = false })
    },
    beforeDestroy () { if (this.unsub) this.unsub() },
    computed: {
      filtered () {
        const q = (this.search || '').toLowerCase()
        const s = (this.statusFilter || 'all').toLowerCase()
        return this.products
          .filter(p => s==='all' ? true : (p.status||'').toLowerCase()===s)
          .filter(p => !q || [p.name, p.summary, p.slug].join(' ').toLowerCase().includes(q))
      },
      sortedFiltered () {
        const arr = [...this.filtered]
        const key = this.sortKey
        const toDate = (ts) => ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds*1000) : null)
        if (key === 'updated-desc') return arr.sort((a,b)=> (toDate(b.updatedAt)-toDate(a.updatedAt)))
        if (key === 'updated-asc')  return arr.sort((a,b)=> (toDate(a.updatedAt)-toDate(b.updatedAt)))
        if (key === 'name-asc')     return arr.sort((a,b)=> String(a.name||'').localeCompare(String(b.name||'')))
        if (key === 'name-desc')    return arr.sort((a,b)=> String(b.name||'').localeCompare(String(a.name||'')))
        return arr
      }
    },
    methods: {
      /* visuals */
      initials (name='') { return name.split(/\s+/).map(x=>x[0]).join('').slice(0,2).toUpperCase() },
      avatarColor (name='') {
        const colors = ['#2d4a3e','#1a2e26','#3d5a4e','#4a6a5e','#5a7a6e','#d4af37','#c9a961','#b8963c']
        const i = Math.abs(name.split('').reduce((a,c)=>a+c.charCodeAt(0),0)) % colors.length
        return colors[i]
      },
      statusColor (s) {
        return (s||'').toLowerCase()==='active' ? '#4caf50'
             : (s||'').toLowerCase()==='paused' ? '#ff9800'
             : 'grey'
      },
      statusIcon (s) {
        const v = (s||'').toLowerCase()
        return v==='active' ? 'mdi-check-circle'
             : v==='paused' ? 'mdi-pause-circle'
             : 'mdi-archive'
      },
      toDate (ts) {
        if (!ts) return null
        if (ts.toDate) return ts.toDate()
        if (ts.seconds) return new Date(ts.seconds * 1000)
        return null
      },
      prettyTs (ts) {
        const d = this.toDate(ts); if (!d) return '—'
        return d.toLocaleString(undefined, { month:'short', day:'2-digit', hour:'numeric', minute:'2-digit' })
      }
    }
  }
  </script>
  
  <style scoped>
  /* ClubLink Colors */
  .gold--text { color: #d4af37 !important; }
  .dark-green--text { color: #2d4a3e !important; }
  
  /* Galaxy backdrop to match the rest of the app */
  .clublink-galaxy{
    position: relative;
    padding-top: 8px;
    background: linear-gradient(135deg, #1a2e26 0%, #2d4a3e 50%, #1a2e26 100%);
    overflow: hidden;
    min-height: 100vh;
  }
  
  .clublink-galaxy::before{
    content:''; 
    position:absolute; 
    inset:0; 
    pointer-events:none;
    background-image: 
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.08) 0%, transparent 50%),
      radial-gradient(2px 2px at 20% 30%, rgba(212, 175, 55, 0.6), transparent 60%),
      radial-gradient(1.5px 1.5px at 70% 10%, rgba(212, 175, 55, 0.5), transparent 60%),
      radial-gradient(1.8px 1.8px at 40% 80%, rgba(212, 175, 55, 0.4), transparent 60%);
    animation: twinkle 6s ease-in-out infinite alternate;
    opacity:.5;
  }
  
  @keyframes twinkle{ from{opacity:.35} to{opacity:.85} }
  
  /* Card glass effect */
  .glass{
    backdrop-filter: saturate(130%) blur(6px);
    background: rgba(255,255,255,.96);
  }
  
  .clublink-card {
    border-top: 4px solid #d4af37 !important;
    position: relative;
    overflow: hidden;
  }
  
  .clublink-card::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(212, 175, 55, 0.03));
    pointer-events: none;
  }
  
  /* Header gradient */
  .clublink-header{
    background: linear-gradient(135deg, #2d4a3e, #1a2e26);
    color:#fff;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }
  
  .gold-divider {
    border-color: rgba(212, 175, 55, 0.2) !important;
    border-width: 2px !important;
  }
  
  /* Search width */
  .search{ max-width: 320px; }
  
  /* Status filter chips */
  .status-chip {
    border: 2px solid rgba(212, 175, 55, 0.2);
    transition: all 0.2s ease;
    cursor: pointer;
  }
  
  .status-chip:hover {
    border-color: rgba(212, 175, 55, 0.4);
    transform: translateY(-2px);
  }
  
  .active-status-chip {
    border-color: transparent !important;
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
  }
  
  /* Row hover / press */
  .hoverable {
    transition: background .15s ease, transform .15s ease, box-shadow .15s ease;
    background: rgba(212, 175, 55, 0.02);
    cursor: pointer;
  }
  
  .hoverable:hover { 
    background: rgba(212, 175, 55, 0.08);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.15);
  }
  
  .truncate { 
    display: -webkit-box; 
    -webkit-line-clamp: 1; 
    -webkit-box-orient: vertical; 
    overflow: hidden; 
  }
  
  .rounded-xl{ border-radius:16px; }
  
  /* Form Inputs */
  .clublink-input-small >>> .v-input__slot {
    background-color: #f8f6f1 !important;
    border-radius: 6px !important;
  }
  
  .clublink-input-small >>> fieldset {
    border-color: #e0e0e0 !important;
  }
  
  .clublink-input-small >>> .v-input--is-focused fieldset {
    border-color: #d4af37 !important;
  }
  </style>